import * as React from "react";
import { Checkbox } from "@bizform/primereact/checkbox";
import { InputSwitch } from "@bizform/primereact/components/inputswitch/InputSwitch";
import { InputSwitchProps } from "@bizform/primereact/inputswitch";
import TooltipOptions from "@bizform/primereact/components/tooltip/TooltipOptions";
import { Static } from "@bizform/type/decorator/Static.decorator";
import { DataType } from "@bizform/type/model/model/DataType.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementDisplayMode,
    ComponentElementStatic,
    ComponentEventType
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { ComponentHelper, Debug, ObjectHelper } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = InputSwitchProps & {
};

type WrappedCompState = {
    // attribute
    id?: string;
    tabindex?: number;
    value?: boolean;
    offLabel?: string;
    onLabel?: string;
    style?: object;
    className?: string;
    printStyle?: object;
    printClassName?: string;
    checked?: boolean;
    disabled?: boolean;
    tooltipAttribute?: TooltipOptions & {text?: string};

    // event
    onChange?(e: ComponentEventType): void;
    onFocus?(event: Event): void;
    onBlur?(event: Event): void;
};

type WrappedFormState = {
    onChange?: string;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
    wrappedFormState: WrappedFormState;
};

/**
 * InputSwitch component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<boolean>>()
export class BfInputSwitch extends AbstractComponentElement<Props, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_kJvBVp3BrxqdShKVQm1obpTv_vQUM3y",
        category: ComponentCategory.BUTTON,
        label: I18n.componentElement.inputswitch.label,
        description: I18n.componentElement.inputswitch.description,
        icon: "",
        dataType: [DataType.BOOLEAN],
        property: null
    };

    focus(): void {
        if (this._inputSwitch) {
            this._inputSwitch.querySelector("input").focus();
        }
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, ComponentEventType>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            this.emitDidCatch(e);
        }
    }

    emit(value: boolean): void {
    }

    emitFormState(value: string): void {
    }

    subscription(value: boolean): void {
        if (ComponentHelper.isNull(value)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: false
                }
            };
            this.setState(newState);
        } else if ( typeof value === "boolean") {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: value
                }
            };
            this.setState(newState);
        } else if (typeof value === "number" && ( value === 0 || value === 1)) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: value ? true : false
                }
            };
            this.setState(newState);
        } else {
            this.emitSubscriptionReject();
        }
    }

    @ReactComponentElementInit(BfInputSwitch)
    static init(): ComponentElement<boolean> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    private _inputSwitch: HTMLInputElement;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                value: false,
                checked: false
            },
            wrappedFormState: {}
        };

        this._onChangeEvent = this._onChangeEvent.bind(this);
        this._setRef = this._setRef.bind(this);
    }

    private _onChangeEvent(e: ComponentEventType): void {
        // change state and then emit
        if (this.state.displayMode === ComponentElementDisplayMode.EDIT) {
            const newState = {
                wrappedCompState: {
                    ...this.state.wrappedCompState,
                    checked: this.accessibility ? e.checked : e.value
                }
            };
            this.setState(newState, this._callbackSetState);
        }
    }

    private _callbackSetState() {
        this.emit(this.state.wrappedCompState.checked ? true : false );
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.emitDidCatch(error);
    }

    emitDidCatch(error: Error | any): void {
        Debug.console.error(error);
    }

    emitSubscriptionReject(): void {
    }

    shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, nextContext: any): boolean {
        return ObjectHelper.hasDifference(nextState, this.state);
    }

    componentDidUpdate() {
        if (this._inputSwitch && this.state.wrappedCompState.tabindex !== this.___tabindex) {
            ComponentHelper.tabindex(this.displayMode, this._inputSwitch.querySelector("input"), this.state.wrappedCompState.tabindex);
            this.___tabindex = this.state.wrappedCompState.tabindex;
        }
    }

    componentWillUnmount(): void {
        this._inputSwitch = null;
    }

    /*
     * 각각의 Component Element는 자신의 DisplayMode에 따라 디바이스에
     * 출력되는 방식이 각각 다르게 표현된다.
     * INIT: Component Element가 초기화 된 상태로서 렌더링되는 대상은 없다(null).
     * EDIT: Component Element가 editable 상태로 렌더링된다.
     * READONLY: Component Element가 readOnly 상태로 렌더링된다.
     * PRINT: Component Element가 print시 자신의 모든 content가 다 보여지도록 렌더링된다.
     */
    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                    return this._render4Edit();
                case ComponentElementDisplayMode.READONLY:
                    return this._render4ReadOnly();
                case ComponentElementDisplayMode.DISABLED:
                    return this._render4Disabled();
                case ComponentElementDisplayMode.PRINT:
                    return this._render4Print();
                default:
                    return null;
            }
        }
    }

    private _render(newProps: any): React.ReactNode {
        const key = ComponentHelper.tooltipProps(newProps, this.elementId, this.accessibility);
        delete(newProps["printClassName"]);
        delete(newProps["printStyle"]);
        if (this.accessibility) {
            return (
                <Checkbox
                    {...newProps}
                    inputId={this.elementId}
                    key={key}
                    ref={this._setRef}
                />
            );
        } else {
            return (
                <InputSwitch
                    {...newProps}
                    key={key}
                    id={this.elementId}
                    ref={this._setRef}
                />
            );
        }
    }

    private _render4Edit(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState
        };

        return this._render(newProps);
    }

    private _render4ReadOnly(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            readOnly: true
        };
        return this._render(newProps);
    }

    private _render4Disabled(): React.ReactNode {
        const newProps = {
            ...this.state.wrappedCompState,
            disabled: true
        };

        return this._render(newProps);
    }

    private _render4Print(): React.ReactNode {
        return (
            <div ref={this._setRef} style={this.state.wrappedCompState ? {...this.state.wrappedCompState.printStyle} : null} className={`bf-print-mode ${this.state.wrappedCompState ? this.state.wrappedCompState.printClassName : null}`}>{`${this.state.wrappedCompState.checked}`}</div>
        );
    }

    private _setRef(ref) {
        if (ref) {
            this._inputSwitch = this.accessibility ? ref.element : ref.container;
            this.readyComponent();
        }
    }
}
